// Resolving one Find RICs row: an input text -> the RIC the terminal's Enter
// would take, the candidates to switch between, and what column C says.
//
// The rules (spec 5.2, 5.4, 5.5; the hub marks pinned items with
// `pinned: true` in an order=rules answer):
//   - an ISIN (shape and check digit): /lookup/isin. The pick is `defaultRic`;
//     the candidates are the active ones, or all of them when none is active.
//   - otherwise /suggest. When any item is pinned (an identifier's own matches,
//     an exact code, the equities whose ticker is the text), the candidates are
//     the pinned items in answer order and the pick is the first of them.
//     When none is, the text is a name: the pick is the first item of the
//     first group and the candidates are that group's items.
//   - "k sur N" marks a row with several candidates, and always marks a name
//     (a name is a guess by nature, even with a single row). An ISIN whose pick
//     is the share's primary listing (`primary: true`) carries none: the other
//     venues are not rivals of it, so it counts as one sure match, like an
//     identifier's. The candidates are still all returned.
//
// The text is sent exactly as typed (a RIC keeps its case: SIEGn.DE).

import { ApiError } from "../lib/api";
import {
  suggest as apiSuggest,
  lookupIsin as apiLookupIsin,
  type IsinAnswer,
  type IsinCandidate,
  type IsinInstrument,
  type SuggestAnswer,
  type SuggestItem,
} from "../lib/searchApi";

export interface Candidate {
  ric: string;
  name: string;
  type: string;
  /** "Inactive", "Called" or "Matured"; unset for an active item. */
  itemStatus?: string;
}

export interface Resolution {
  status: "ok" | "none" | "unavailable";
  ric?: string;
  name?: string;
  type?: string;
  /** "Inactive", "Called" or "Matured" (the pick's). */
  itemStatus?: string;
  /** The pick's 1-based position among the candidates; set with `n` only when column C carries a marker. */
  k?: number;
  n?: number;
  partial?: boolean;
  reason?: string;
  candidates: Candidate[];
}

export interface ResolveDeps {
  suggest(q: string, bulk: boolean): Promise<SuggestAnswer>;
  lookupIsin(isin: string, bulk: boolean): Promise<IsinAnswer>;
  sleep(ms: number): Promise<void>;
}

const defaultDeps: ResolveDeps = {
  suggest: apiSuggest,
  lookupIsin: apiLookupIsin,
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

/** A hub 429 is retried this many times (after the first try) before the row is unavailable. */
export const MAX_RETRIES = 3;
/** The wait when a 429 carried no Retry-After. */
export const DEFAULT_RETRY_MS = 2000;

const ISIN_SHAPE = /^[A-Za-z]{2}[A-Za-z0-9]{9}[0-9]$/;

/**
 * An ISIN, as the hub's is_isin has it: two letters, nine letters or digits,
 * one digit, and a right check digit (letters count as 10..35, then Luhn over
 * the digits). `FR0014018IL7` has the shape and is not one.
 */
export function isIsin(text: string): boolean {
  if (!ISIN_SHAPE.test(text)) return false;
  let digits = "";
  for (const c of text.toUpperCase()) digits += parseInt(c, 36).toString();
  let total = 0;
  for (let i = 0; i < digits.length; i++) {
    let d = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    total += d;
  }
  return total % 10 === 0;
}

async function withRetry<T>(call: () => Promise<T>, sleep: (ms: number) => Promise<void>): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await call();
    } catch (e) {
      if (!(e instanceof ApiError) || e.status !== 429 || attempt >= MAX_RETRIES) throw e;
      await sleep(e.retryAfterMs ?? DEFAULT_RETRY_MS);
    }
  }
}

const unavailable = (reason: string): Resolution => ({ status: "unavailable", reason, candidates: [] });
const none = (): Resolution => ({ status: "none", candidates: [] });
/** An answer cut short that gives no row cannot say "nothing found": it is to be run again. */
const INCOMPLETE = "réponse incomplète du hub";

function reasonOf(e: unknown): string {
  const m = e instanceof Error ? e.message : String(e);
  return m || "erreur inconnue";
}

function isActive(c: IsinCandidate): boolean {
  return c.active ?? c.assetState === "AC";
}

const INSTRUMENT_STATUS: Record<string, string> = { called: "Called", matured: "Matured", inactive: "Inactive" };

/** The status word of a candidate of an ISIN: the instrument's own, else the candidate's activity. */
function isinStatus(c: IsinCandidate, instrument?: IsinInstrument | null): string | undefined {
  const fromInstrument = instrument ? INSTRUMENT_STATUS[instrument.status] : undefined;
  if (fromInstrument) return fromInstrument;
  return isActive(c) ? undefined : "Inactive";
}

/** Builds an "ok" resolution from its pick and candidates (the pick is among them). */
function ok(pick: Candidate, candidates: Candidate[], marked: boolean, partial: boolean): Resolution {
  const r: Resolution = {
    status: "ok",
    ric: pick.ric,
    name: pick.name,
    type: pick.type,
    candidates,
  };
  if (pick.itemStatus) r.itemStatus = pick.itemStatus;
  if (marked) {
    r.k = candidates.findIndex((c) => c.ric === pick.ric) + 1;
    r.n = candidates.length;
  }
  if (partial) r.partial = true;
  return r;
}

function fromIsin(a: IsinAnswer): Resolution {
  const partial = !!a.partial;
  const all = a.candidates ?? [];
  const instrument = a.instrument ?? null;
  const toCandidate = (c: IsinCandidate): Candidate => {
    const itemStatus = isinStatus(c, instrument);
    const out: Candidate = { ric: c.ric, name: c.name, type: instrument?.type || c.category };
    if (itemStatus) out.itemStatus = itemStatus;
    return out;
  };

  const ric = a.defaultRic || instrument?.ric || all[0]?.ric || "";
  if (!ric) return partial ? unavailable(INCOMPLETE) : none();

  const active = all.filter(isActive);
  const chosen = (active.length ? active : all).map(toCandidate);
  let pick = chosen.find((c) => c.ric === ric);
  if (!pick) {
    // defaultRic is not among the candidates (inactive while others are
    // active, or a master record with a code of its own): it is still the
    // pick, and it heads the list so that "k sur N" stays true.
    const row = all.find((c) => c.ric === ric);
    pick = row
      ? toCandidate(row)
      : {
          ric,
          name: instrument?.name ?? "",
          type: instrument?.type ?? "",
          ...(instrument && INSTRUMENT_STATUS[instrument.status] ? { itemStatus: INSTRUMENT_STATUS[instrument.status] } : {}),
        };
    chosen.unshift(pick);
  }
  // A share's primary listing is a sure pick among its venues: no marker.
  const primary = all.find((c) => c.ric === pick.ric)?.primary === true;
  return ok(pick, chosen, chosen.length > 1 && !primary, partial);
}

function suggestCandidate(it: SuggestItem): Candidate {
  const c: Candidate = { ric: it.ric, name: it.name, type: it.type };
  if (it.active === false) c.itemStatus = "Inactive";
  return c;
}

function fromSuggest(a: SuggestAnswer): Resolution {
  const partial = !!a.partial;
  const groups = (a.groups ?? []).filter((g) => g.items && g.items.length > 0);
  if (!groups.length) return partial ? unavailable(INCOMPLETE) : none();

  const pinned: SuggestItem[] = [];
  for (const g of groups) for (const it of g.items) if (it.pinned === true) pinned.push(it);
  if (pinned.length) {
    const candidates = pinned.map(suggestCandidate);
    return ok(candidates[0], candidates, candidates.length > 1, partial);
  }
  // A name: the pick's group. Always marked, even with a single row.
  const candidates = groups[0].items.map(suggestCandidate);
  return ok(candidates[0], candidates, true, partial);
}

export async function resolveRow(text: string, deps: ResolveDeps = defaultDeps): Promise<Resolution> {
  try {
    if (isIsin(text)) {
      return fromIsin(await withRetry(() => deps.lookupIsin(text, true), deps.sleep));
    }
    return fromSuggest(await withRetry(() => deps.suggest(text, true), deps.sleep));
  } catch (e) {
    // The add-in's own session is over (api.ts signed the user out): not a
    // row's failure. The caller stops the run instead of marking every row.
    if (isSignedOut(e)) throw e;
    return unavailable(reasonOf(e));
  }
}

/** A 401 that is the add-in's (not the hub's own, `detail.kind === "hub"`). */
export function isSignedOut(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401 && e.detail?.kind !== "hub";
}
